import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Checkbox, Input, InputOtp, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from '@heroui/react';
import Notiflix from "notiflix";

const ForgotPasswordModel = ({ isOpen, onOpenChange }) => {

    const BASE_URL = "https://oxfordcollege.lk/api/"
    const END_POINT = BASE_URL + "routes/";

    const [showVerificationFields, setShowVerificationFields] = useState(false);
    const [showFields, setShowFields] = useState(false);
    const [verificationButtonText, setVerificationButtonText] = useState('Send Verification Code');
    const [isStudent, setIsStudent] = useState(false);

    const [formData, setFormData] = useState({
        admissionNumber: null,
        email: '',
        verificationCode: '',
        newPassword: '',
        confirmPassword: ''
    })

    const handleSendingMail = async () => {
        Notiflix.Loading.pulse('Sending verification Mail...');

        if (formData.email === '') {
            Notiflix.Loading.remove();
            Notiflix.Notify.failure('Please fill all the required fields');
            return;
        }

        try {
            let bearer = '';
            if (document.cookie.split('jwt=')[1]) {
                bearer = `Bearer ${document.cookie.split('jwt=')[1]?.split(';')[0]}`;
            }
            const response = await fetch(END_POINT + 'auth.php?route=forgot-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${bearer}`
                },
                body: JSON.stringify({
                    email: formData.email,
                    admissionNumber: formData.admissionNumber
                })
            });

            const respData = await response.json();

            if (!response.ok || respData.status !== 200) {
                Notiflix.Loading.remove();
                Notiflix.Notify.failure('Failed to send verification mail');
                setVerificationButtonText('Send Again');
                return;
            }

            Notiflix.Loading.remove();
            Notiflix.Notify.success('Verification mail sent');
            setShowVerificationFields(true);
            setVerificationButtonText('Send Again');

        } catch (error) {
            console.error("Something went wrong : ", error);
        }
    }

    const handleResetPassword = async () => {
        Notiflix.Loading.pulse('Resetting Password...');

        if (formData.verificationCode === '' || formData.newPassword === '' || formData.confirmPassword === '') {
            Notiflix.Loading.remove();
            Notiflix.Notify.failure('Please fill all the required fields');
            return;
        }

        if (formData.newPassword !== formData.confirmPassword) {
            Notiflix.Loading.remove();
            Notiflix.Notify.failure('Passwords do not match');
            return;
        }

        try {
            let bearer = '';
            if (document.cookie.split('jwt=')[1]) {
                bearer = `Bearer ${document.cookie.split('jwt=')[1]?.split(';')[0]}`;
            }
            const response = await fetch(END_POINT + 'auth.php?route=forgot-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `${bearer}`
                },
                body: JSON.stringify({
                    email: formData.email,
                    verificationCode: formData.verificationCode,
                    newPassword: formData.newPassword,
                    admissionNumber: formData.admissionNumber
                })
            });
            if (!response.ok) {
                Notiflix.Loading.remove();
                Notiflix.Notify.failure('Password reset failed');
                return;
            }

            Notiflix.Loading.remove();
            Notiflix.Notify.success('Password reset successful');
            onOpenChange(false);

        } catch (error) {
            console.error("Something went wrong : ", error);
            Notiflix.Loading.remove();
            Notiflix.Notify.failure('Something went wrong. Please try again later');
        }
    }

    return (
        <div>
            <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
                <ModalContent>
                    <>
                        <ModalHeader className="flex flex-col gap-1">Reset Password</ModalHeader>
                        <ModalBody>
                            <div className='flex flex-col gap-4'>
                                <Input
                                    isRequired
                                    variant='faded'
                                    label="Email"
                                    placeholder="Enter your email"
                                    name="email"
                                    value={formData.email}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    type="email"
                                    description="We'll send you a verification code to your email. Please check your inbox."
                                />

                                {isStudent && (
                                    <Input
                                        variant='faded'
                                        label="Admission Number"
                                        placeholder="Enter your admission number"
                                        name="admissionNumber"
                                        value={formData.admissionNumber}
                                        onChange={(e) => setFormData({ ...formData, admissionNumber: e.target.value })}
                                        type="text"
                                        description="Enter your admission number if you are a student."
                                    />
                                )}

                                <Checkbox name="isStudent" checked={isStudent} onChange={(e) => setIsStudent(e.target.checked)}>
                                    <span className='text-neutral-400 text-sm'>I am a student</span>
                                </Checkbox>

                                <Button color='primary' onPress={handleSendingMail}>
                                    {verificationButtonText}
                                </Button>

                                {showVerificationFields && (
                                    <>
                                        <hr />

                                        <span className='text-neutral-400 text-sm'>Enter the verification code sent to your email</span>

                                        <InputOtp
                                            isRequired
                                            variant='faded'
                                            length={6}
                                            name="verificationCode"
                                            validationBehavior="native"
                                            placeholder="Enter your verification code"
                                            size='lg'
                                            onComplete={
                                                (e) => {
                                                    setShowFields(true);
                                                    setFormData({ ...formData, verificationCode: e });
                                                }
                                            }
                                        />
                                    </>
                                )}

                                {showFields && (
                                    <>

                                        <hr />

                                        <Input
                                            isRequired
                                            variant='faded'
                                            label="New Password"
                                            placeholder="Enter your new password"
                                            name="newPassword"
                                            onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                                            type="password"
                                        />
                                        <Input
                                            isRequired
                                            variant='faded'
                                            label="Confirm Password"
                                            placeholder="Confirm your new password"
                                            name="confirmPassword"
                                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                            type="password"
                                        />
                                    </>
                                )}
                            </div>
                        </ModalBody>
                        <ModalFooter>
                            {showFields && (
                                <Button onPress={handleResetPassword} className='bg-black text-white w-full'>
                                    Reset Password
                                </Button>
                            )}
                        </ModalFooter>
                    </>
                </ModalContent>
            </Modal>

        </div>
    )
}

export default ForgotPasswordModel

ForgotPasswordModel.propTypes = {
    isOpen: PropTypes.bool,
    onOpenChange: PropTypes.func
}


